import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getAppliedPracticeHistory, type AppliedPracticeHistoryItem } from '../services/conceptPlus1Service';
import { ArrowLeft, ClipboardList, Calendar, ChevronRight } from 'lucide-react';
import { Card } from '@branon-shared/ui/Card';
import { IconBadge } from '@branon-shared/ui/IconBadge';
import { EmptyState } from '@branon-shared/ui/EmptyState';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const DIFFICULTY_STYLE: Record<string, string> = {
  Basic: 'bg-success-soft text-success',
  Standard: 'bg-marigold/25 text-ink',
  Advanced: 'bg-night/10 text-night',
};

export default function AppliedPracticeHistory() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<AppliedPracticeHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) return;
    getAppliedPracticeHistory(session.student_id).then((data) => {
      setItems(data);
      setLoading(false);
    });
  }, [session?.student_id]);

  if (isLoading || loading) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading your practice history…</p>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-24 pb-8">
      <Link to="/exam-prep-plan" className="inline-flex items-center gap-1.5 text-sm text-coral hover:text-ink font-medium mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Exam Prep
      </Link>

      <h1 className="font-quicksand text-xl font-bold text-ink mb-1">Practice History</h1>
      <p className="text-sm text-gray-500 mb-6">{items.length} set{items.length === 1 ? '' : 's'} — revisit any practice sheet you've worked through</p>

      {items.length === 0 ? (
        <Card>
          <EmptyState
            icon={ClipboardList}
            title="No practice sets yet"
            subtitle="Answer some Applied Practice questions on a concept page and they'll show up here."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <button
              key={`${item.concept_id}-${item.difficulty}`}
              onClick={() => navigate(`/practice-history/${item.concept_id}/${item.difficulty}`)}
              className="w-full text-left block"
            >
              <Card className="hover:-translate-y-0.5 transition-transform" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <IconBadge icon={ClipboardList} tone="night" size={36} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-quicksand font-bold text-ink text-sm">{item.concept_name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${DIFFICULTY_STYLE[item.difficulty] ?? 'bg-gray-100 text-gray-700'}`}>
                        {item.difficulty}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(item.last_attempt_at)}
                      </span>
                      <span>{item.correct_count} of {item.attempted_count} correct{item.attempted_count < item.total_questions ? ` (${item.total_questions - item.attempted_count} left)` : ''}</span>
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </Card>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
